import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
} from "@mui/material";
import styled from "styled-components";
import CloseIcon from "@mui/icons-material/Close";
import api from "../../api/config";
import { DeleteStudy } from "../../api/Inbound";
import { makeStyles } from "@mui/styles";
import { useNotification } from "../../context/NotificationContext";

const Header = styled(DialogTitle)`
  background-color: #28929c;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  font-size: 16px;
`;

const Content = styled(DialogContent)`
  background-color: #2b3342;
  color: white;
  padding: 16px;
`;

const StyledTableHeaderCell = styled(TableCell)`
  background-color: #3e526a !important;
  color: white;
`;

const StyledTableCell = styled(TableCell)`
  background-color: #2b3342 !important;
  color: white;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
`;

const useStyles = makeStyles({
  headerCell: {
    backgroundColor: "#3E526A !important",
    color: "#A5E0FF !important",
    textTransform: "capitalize",
    fontWeight: 600,
    fontSize: "14px",
  },
  tableRow: {
    "& .MuiTableCell-head": {
      backgroundColor: "#3E526A !important",
      color: "#A5E0FF !important",
      textTransform: "capitalize",
      fontWeight: 600,
      fontSize: "14px",
    },
  },
});

const PatientNotFoundModal = ({ open, onClose, selectedStudies }) => {
  const [suggestedMatches, setSuggestedMatches] = useState({});
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [forceDeleteQueue, setForceDeleteQueue] = useState([]);
  const [errorMessages, setErrorMessages] = useState({});
  const classes = useStyles();
  const { showNotification } = useNotification();

  useEffect(() => {
    if (open) {
      setSuggestedMatches({});
      setForceDeleteQueue([]);
      setErrorMessages({});
    }
  }, [open]);

  const deleteStudy = async (study) => {
    setDeleting(true);
    try {
      console.log("Attempting to delete study:", study);
      const response = await api.post(
        `${DeleteStudy}/${study.id}?force_delete=true`
      );
      console.log("Delete response:", response.status);

      if (response.status === 200) {
        setSuggestedMatches((prev) => {
          const updatedMatches = { ...prev };
          delete updatedMatches[study.id];
          return updatedMatches;
        });
      }
    } catch (error) {
      if (error.response && error.response.status === 460) {
        console.log("Got 460 - Possible matches found:", error.response.data);
        const { error: errorMsg, patients } = error.response.data;
        setErrorMessages((prev) => ({
          ...prev,
          [study.id]: errorMsg,
        }));
        setSuggestedMatches((prev) => ({
          ...prev,
          [study.id]: patients || [],
        }));
        showNotification("Possible matches found", "warning");
      } else {
        showNotification("Error deleting study", "error");
      }
    }
    setDeleting(false);
  };

  const deleteAllStudies = async () => {
    setDeleting(true);
    let newForceDeleteQueue = [];
    for (const study of selectedStudies) {
      try {
        const response = await api.post(
          `${DeleteStudy}/${study.id}?force_delete=true`
        );
        if (response.status === 460) {
          const { error: errorMsg, patients } = response.data;
          newForceDeleteQueue.push(study.id);
          setErrorMessages((prev) => ({
            ...prev,
            [study.id]: errorMsg,
          }));
          setSuggestedMatches((prev) => ({
            ...prev,
            [study.id]: patients || [],
          }));
        } else {
          showNotification("Studies Deleted Successfully", "success");
        }
      } catch (error) {
        console.error("Error deleting study:", error);
        showNotification("Error deleting study", "error");
      }
    }
    setForceDeleteQueue(newForceDeleteQueue);
    setDeleting(false);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <Header>
        Patient Not Found
        <IconButton onClick={onClose} style={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </Header>
      <Content>
        {loading && (
          <LoadingOverlay>
            <CircularProgress
              size={24}
              style={{ color: "white", marginBottom: "10px" }}
            />
            Fetching possible matches...
          </LoadingOverlay>
        )}
        <Typography style={{ color: "white", marginTop: "10px" }}>
          Studies to be deleted:
        </Typography>
        {selectedStudies.map((study) => (
          <div key={study.id} style={{ marginBottom: "20px" }}>
            <Typography sx={{ mt: 2, color: "#A5E0FF" }}>
              Study: {study.patientName} (DOB: {study.patientDOB})
            </Typography>
            {errorMessages[study.id] && (
              <Typography
                variant="body2"
                sx={{ mt: 1, color: "red", fontStyle: "italic" }}
              >
                {errorMessages[study.id]}
              </Typography>
            )}

            {suggestedMatches[study.id] &&
              suggestedMatches[study.id].length > 0 && (
                <>
                  <Typography variant="body2" sx={{ mt: 2, color: "#FFA726" }}>
                    Possible matches found:
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow className={classes.tableRow}>
                          <StyledTableHeaderCell>
                            Suggested Patient Name
                          </StyledTableHeaderCell>
                          <StyledTableHeaderCell>
                            Date of Birth
                          </StyledTableHeaderCell>
                          <StyledTableHeaderCell>Gender</StyledTableHeaderCell>
                          <StyledTableHeaderCell>Action</StyledTableHeaderCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {suggestedMatches[study.id].map((patient, index) => (
                          <TableRow key={index}>
                            <StyledTableCell>
                              {patient.first_name} {patient.middle_name}{" "}
                              {patient.last_name}
                            </StyledTableCell>
                            <StyledTableCell>
                              {patient.date_of_birth}
                            </StyledTableCell>
                            <StyledTableCell>{patient.gender}</StyledTableCell>
                            <StyledTableCell>
                              <Button
                                variant="contained"
                                size="small"
                                // onClick={() => forceDeleteStudy(study)}
                                disabled={deleting}
                              >
                                Force Match
                              </Button>
                            </StyledTableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
          </div>
        ))}
      </Content>
      <DialogActions sx={{ backgroundColor: "#28929C", padding: "12px" }}>
        <Button onClick={onClose} color="primary" variant="contained">
          Close
        </Button>
        {selectedStudies.length > 1 && (
          <Button
            onClick={deleteAllStudies}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            Force Delete All
          </Button>
        )}
        {selectedStudies.length === 1 && (
          <Button
            onClick={() => deleteStudy(selectedStudies[0])}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            Force Delete
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PatientNotFoundModal;
